/**
 * App-wide "hide P&L" preference (privacy toggle for screen-sharing / photos).
 * One module-level store so every `PnlFlash` re-renders together when any
 * toggle flips it — no provider needed. Persisted per browser in
 * `localStorage`; storage failures fall back silently to "shown".
 */
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'hide_pnl';
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

let hidden = read();

export function setHidePnl(next: boolean): void {
  hidden = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* preference just won't survive a reload */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useHidePnl(): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(subscribe, () => hidden);
  return [value, setHidePnl];
}
